import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalHeader } from '@creditornot/cb-modal';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { AggregatedPurchasedItemOptionValue, PurchaseItem } from 'apis/merchant-gateway/merchant-analytics/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography, typographyCss } from 'common/typography';
import { localize } from 'common/utils';
import { useVenueContext } from 'modules/venue/VenueContext';

import ItemOptionsSection from './ItemOptionsSection';
import { getOptionsTotalPrice } from './utils';

const Root = styled.div`
  color: ${cbSemanticCssVars.textPrimary};
  /* NOTE: Extra padding to fit the Intercom chat bubble */
  padding-block-end: 70px;
`;

const Section = styled.div`
  margin-block-end: 24px;
  padding: 0 24px;
`;

const Text = styled.div`
  ${typographyCss.Body3};
  letter-spacing: 0.15px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const MultiplicationSign = styled.div`
  ::before {
    content: '×';
    margin: 0 4px;
    font-size: 13px;
  }
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  gap: 16px;
`;

const ItemDescription = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const ItemCount = styled(Text)`
  display: flex;
  min-width: 36px;
  font-weight: bold;
`;

const ItemName = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemPrice = styled(Text)``;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
`;

const TotalLabel = styled(Text)``;

const TotalBoldLabel = styled(Text)`
  font-weight: bold;
`;

const TotalAmount = styled(Text)``;

const TotalBoldAmount = styled(Text)`
  font-weight: bold;
`;

type PurchaseItemDetailsV2Props = {
  item: PurchaseItem;
  onClose: () => void;
  itemOptionsV2: AggregatedPurchasedItemOptionValue[];
};
const PurchaseItemDetailsV2 = ({ item, onClose, itemOptionsV2 }: PurchaseItemDetailsV2Props) => {
  const { currency } = useVenueContext();
  const { locale } = useIntl();

  return (
    <Root>
      <ModalHeader onCloseClick={onClose}>
        <Typography.Heading6>
          <FormattedMessage id="purchases.headings.item" />
        </Typography.Heading6>
      </ModalHeader>

      <Section>
        <Item>
          <ItemDescription>
            <ItemCount>
              {item.count} <MultiplicationSign />
            </ItemCount>
            <ItemName>{localize(item.product.names, locale)}</ItemName>
          </ItemDescription>
          <ItemPrice>
            <MonetaryAmount
              amount={item.pricing.itemOnlyPrice.gross + item.pricing.itemDeposit.gross}
              currency={currency}
            />
          </ItemPrice>
        </Item>
      </Section>
      {itemOptionsV2.length > 0 && (
        <Section>
          <ItemOptionsSection itemOptions={itemOptionsV2} />
          <Total>
            <TotalLabel>
              <FormattedMessage id="purchases.labels.options-subtotal" values={{ currency }} />
            </TotalLabel>
            <TotalAmount>
              <MonetaryAmount amount={getOptionsTotalPrice(itemOptionsV2)} currency={currency} />
            </TotalAmount>
          </Total>
        </Section>
      )}
      <Section>
        <Total>
          <TotalBoldLabel>
            <FormattedMessage id="purchases.labels.item-total" values={{ currency }} />
          </TotalBoldLabel>
          <TotalBoldAmount>
            <MonetaryAmount amount={item.pricing.beforeDiscountPrice.gross} currency={currency} />
          </TotalBoldAmount>
        </Total>
        <Total>
          <TotalLabel>
            <FormattedMessage id="purchases.discount.sponsored" />
          </TotalLabel>
          <TotalAmount>
            <MonetaryAmount amount={item.pricing.totalDiscount.gross} currency={currency} />
          </TotalAmount>
        </Total>
        <Total>
          <TotalBoldLabel>
            <FormattedMessage id="purchases.columns.total-after-deduction" />
          </TotalBoldLabel>
          <TotalBoldAmount>
            <MonetaryAmount amount={item.pricing.finalPrice.gross} currency={currency} />
          </TotalBoldAmount>
        </Total>
      </Section>
    </Root>
  );
};

export default PurchaseItemDetailsV2;
